import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSubscriptionQuery, useGetMetaBuilderAuthStatusQuery } from "../apis/metaBuilderApi";
import { ROUTES } from "../consts/routes.consts";

interface SubscriptionProtectedPageProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that protects pages requiring an active subscription.
 * Redirects to wallet page immediately if subscription is not active or has expired.
 */
const SubscriptionProtectedPage: React.FC<SubscriptionProtectedPageProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { data: authStatusData } = useGetMetaBuilderAuthStatusQuery();
  const metaUserId = authStatusData?.userId || localStorage.getItem("id") || "";
  const { data: subscriptionData, isLoading: isSubscriptionLoading } =
    useGetSubscriptionQuery(metaUserId, {
      skip: !metaUserId,
    });

  const [isReady, setIsReady] = useState(false);
  const [hasShownToast, setHasShownToast] = useState(false);

  // Determine if user has active subscription
  const hasActiveSubscription = React.useMemo(() => {
    if (!subscriptionData?.subscription) return false;
    if (subscriptionData.expired) return false;

    const status = subscriptionData.subscription.status?.toLowerCase();
    return status === "active" || status === "trialing";
  }, [subscriptionData]);

  // Handle redirect logic
  useEffect(() => {
    if (isSubscriptionLoading) {
      setIsReady(false);
      return;
    }

    // If subscription is not active, redirect immediately and set flag for toast
    if (metaUserId && !hasActiveSubscription && !hasShownToast) {
      setHasShownToast(true);
      // Set flag to show toast on wallet page
      localStorage.setItem("showSubscriptionRequiredToast", "true");
      navigate(ROUTES.META_ADS_WALLET);
    } else {
      setIsReady(true);
    }
  }, [isSubscriptionLoading, metaUserId, hasActiveSubscription, hasShownToast, navigate]);

  // Loading state
  if (isSubscriptionLoading) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center bg-[#050505] text-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#58F2A9] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // If not ready (no userId or data not loaded), show loading
  if (!isReady) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center bg-[#050505] text-white">
        <p className="text-white/60 text-lg">Loading...</p>
      </div>
    );
  }

  // Only render children if subscription is active
  return <>{children}</>;
};

export default SubscriptionProtectedPage;
